import { EventBus }  from '../../core/EventBus.js';
import { GameState } from '../../core/GameState.js';
import { Modal }     from '../components/Modal.js';
import { serverIntent } from '../../core/serverIntent.js';
import { STARTING_PORT_IDS } from '../../config/ports.js';
import { COMPANY_COLOR_PALETTE } from '../../config/constants.js';
import { companySetupFieldsHtml, bindCompanySetupFields } from '../components/CompanySetupFields.js';

/**
 * Game menu modal — Settings, Help & Guide.
 * Listens to `ui:menuOpened` via EventBus.
 *
 * Phase 6: Save/Load removed. Progress is saved server-side automatically.
 * "New Game" removed — accounts are persistent in the shared world.
 * Speed controls section removed from help (no pause in Phase 6).
 */
export class GameMenuModal {
  constructor(saveManager) {
    // saveManager param retained for backward-compat; unused in Phase 6
    this.modal = new Modal('game-menu-modal', { title: 'Menu' });
    this.view = 'main';
    this.helpTab = 'start';

    EventBus.on('ui:menuOpened', () => this.open());
  }

  open() {
    this.view = 'main';
    this._render();
    this.modal.open();
  }

  _render() {
    if (this.view === 'help') {
      this._renderHelp();
    } else if (this.view === 'reset') {
      this._renderResetSetup();
    } else {
      this._renderMain();
    }
  }

  _renderMain() {
    const s = GameState.settings;
    this.modal.setTitle('Menu');
    this.modal.setBody(`
      <div class="menu-info">
        <p style="color:var(--text-muted,#8899aa);font-size:.85rem;margin:.5rem 0 1rem">
          Your progress is saved automatically on the server.
        </p>
      </div>
      <div class="menu-settings">
        <h4>Settings</h4>
        <label class="menu-toggle">
          <input type="checkbox" id="gmm-analytics" ${s.sendAnalytics ? 'checked' : ''}>
          Send anonymous usage statistics
        </label>
        <p style="color:var(--text-muted,#8899aa);font-size:.78rem;margin:.6rem 0 0">
          Company name and color have moved to the Finance tab's Company sub-tab.
        </p>
      </div>
      <div class="menu-buttons">
        <button class="menu-btn" id="gmm-help">Help &amp; Guide</button>
        <button class="menu-btn" id="gmm-close">Close</button>
      </div>
      <div class="menu-danger-zone" style="margin-top:1.5rem;padding-top:1rem;border-top:1px solid var(--border,#1e2d45)">
        <button class="menu-btn menu-btn-danger" id="gmm-reset" style="width:100%;color:#e74c3c;border-color:#e74c3c">
          Reset My Game
        </button>
        <p style="color:var(--text-muted,#8899aa);font-size:.78rem;margin:.4rem 0 0">
          Wipes your money, ships, cargo, research, contracts, warehouses, and crew back to a
          brand-new start. Your account/login is not affected.
        </p>
      </div>`);

    document.getElementById('gmm-help').onclick = () => {
      this.view = 'help';
      this._render();
    };
    document.getElementById('gmm-close').onclick = () => this.modal.close();

    document.getElementById('gmm-analytics').onchange = (e) => {
      s.sendAnalytics = e.target.checked;
    };

    document.getElementById('gmm-reset').onclick = () => {
      this.view = 'reset';
      // Pre-fill with the player's CURRENT setup — "start with the same
      // setup as before" is just "don't touch anything and confirm";
      // any field can still be changed like a fresh registration's setup.
      if (!this._resetSetup) {
        this._resetSetup = {
          companyName:  GameState.player?.companyName || '',
          companyColor: GameState.player?.companyColor || COMPANY_COLOR_PALETTE[0],
          startPortId:  STARTING_PORT_IDS[0]
        };
      }
      this._render();
    };
  }

  _renderResetSetup() {
    this.modal.setTitle('Reset My Game');

    this.modal.setBody(`
      <p style="color:#e74c3c;font-weight:600;margin:0 0 .75rem;line-height:1.5">
        This permanently wipes your money, ships, cargo, research, contracts, warehouses, and crew
        back to a brand-new start. Your login is not affected. This cannot be undone.
      </p>
      ${companySetupFieldsHtml('gmm-reset', this._resetSetup)}
      <div class="menu-buttons" style="margin-top:1.25rem">
        <button class="menu-btn" id="gmm-reset-cancel">Cancel</button>
        <button class="menu-btn menu-btn-danger" id="gmm-reset-confirm" style="color:#e74c3c;border-color:#e74c3c">Reset</button>
      </div>`);

    bindCompanySetupFields('gmm-reset', this.modal.el, this._resetSetup);

    document.getElementById('gmm-reset-cancel').onclick = () => {
      this.view = 'main';
      this._render();
    };
    document.getElementById('gmm-reset-confirm').onclick = () => this._resetGame();
  }

  async _resetGame() {
    const result = await serverIntent('player/reset', {
      startPortId:  this._resetSetup.startPortId,
      companyName:  this._resetSetup.companyName.trim() || undefined,
      companyColor: this._resetSetup.companyColor
    });
    if (result.success) {
      this.modal.close();
      // The reset company setup was just consumed — clear it so the NEXT
      // time the reset screen opens, it re-pre-fills from the (now fresh)
      // player state instead of showing this stale form again.
      this._resetSetup = null;
      EventBus.emit('ui:notification', { message: 'Your game has been reset.', type: 'success' });
      // The server pushes a fresh full snapshot over the same WS connection
      // (server/transport/intents/player.js) — GameState re-hydrates from
      // it automatically via the existing onSnapshot handler in main.js, no
      // extra client-side wiring needed here.
    }
  }

  _renderHelp() {
    const tabs = [
      { id: 'start',     label: 'Getting Started' },
      { id: 'trading',   label: 'Trading' },
      { id: 'ships',     label: 'Ships & Fuel' },
      { id: 'crew',      label: 'Crew' },
      { id: 'contracts', label: 'Contracts & Research' },
      { id: 'controls',  label: 'Controls' },
    ];

    this.modal.setTitle('Help & Guide');
    this.modal.setBody(`
      <div class="help-tabs">
        ${tabs.map(t => `<button class="help-tab${t.id === this.helpTab ? ' active' : ''}" data-tab="${t.id}">${t.label}</button>`).join('')}
      </div>
      <div class="help-content" id="gmm-help-content">
        ${this._helpContent(this.helpTab)}
      </div>
      <div class="menu-buttons">
        <button class="menu-btn" id="gmm-help-back">&larr; Back</button>
      </div>`);

    this.modal.el.querySelectorAll('.help-tab').forEach(btn => {
      btn.onclick = () => {
        this.helpTab = btn.dataset.tab;
        this.modal.el.querySelectorAll('.help-tab').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        document.getElementById('gmm-help-content').innerHTML = this._helpContent(this.helpTab);
      };
    });

    document.getElementById('gmm-help-back').onclick = () => {
      this.view = 'main';
      this._render();
    };
  }

  _helpContent(tab) {
    switch (tab) {
      case 'start': return `
        <h4>Welcome to SeaTrader</h4>
        <p>You start with $100,000 and a small Coastal Trader (50 TEU capacity). Your goal is to build a shipping empire by trading cargo between ports around the world.</p>
        <p>SeaTrader is a <strong>persistent shared world</strong> — the simulation runs 24/7 even when you're offline. When you reconnect you'll see your ships exactly where the world left them.</p>
        <h4>The Interface</h4>
        <ul>
          <li><strong>Map</strong> — click any port marker to select it and view details</li>
          <li><strong>Side panel</strong> — shows the selected port's trade goods, services, and info</li>
          <li><strong>Bottom tabs</strong> — Fleet, Contracts, Market, Research, and Finance</li>
          <li><strong>Live indicator</strong> — top-left shows your connection status (green = connected)</li>
        </ul>
        <h4>First Steps</h4>
        <ul>
          <li>Click a nearby port on the map to see what cargo is available</li>
          <li>Buy cheap cargo from the port's <strong>Trade</strong> tab (look for produced goods)</li>
          <li>Set a destination port that demands that cargo and send your ship</li>
          <li>Sell the cargo at the destination for a profit</li>
          <li>Use the <strong>Market</strong> tab to compare prices across ports</li>
        </ul>`;

      case 'trading': return `
        <h4>Supply &amp; Demand</h4>
        <p>Each port <strong>produces</strong> certain goods (large stock, low prices) and <strong>demands</strong> others (small stock, high prices). Buy where it's produced, sell where it's demanded.</p>
        <h4>Shared Market</h4>
        <p>Prices are shared across all players. Your trades and other players' trades move the same market prices in real time. Watch out for price impacts when multiple traders converge on the same routes.</p>
        <h4>Port Stock</h4>
        <p>Ports have limited stock that depletes as you buy. Stock refreshes gradually each game day. If a port runs out, you'll need to wait or look elsewhere.</p>
        <h4>Cargo Categories</h4>
        <ul>
          <li><strong>Bulk dry</strong> (cargo ships) — raw materials, grain, steel, coal, timber, etc.</li>
          <li><strong>Container</strong> (cargo/express) — electronics, textiles, luxury goods, spices, etc.</li>
          <li><strong>Bulk liquid</strong> (tankers) — crude oil, refined oil, LNG, chemicals</li>
          <li><strong>Refrigerated</strong> (reefers) — meat, seafood, dairy, fruit, medical supplies</li>
          <li><strong>Roll-on/Roll-off</strong> (cargo/RoRo) — automobiles, trucks, machinery</li>
        </ul>
        <div class="help-callout">Not every ship can carry every cargo type. Check the ship's category and the cargo's requirements before loading.</div>
        <h4>Loading &amp; Unloading</h4>
        <p>Moving cargo takes time. Big ports have more cranes and work faster than small ones, RoRo ships load in half the time, and port strikes slow everything down. If you order a ship to depart while it's still loading, it sails as soon as the cranes are done. Logistics research makes loading both faster and cheaper.</p>`;

      case 'ships': return `
        <h4>Ship Categories</h4>
        <ul>
          <li><strong>Cargo</strong> — general-purpose; carry containers, bulk dry, and RoRo goods</li>
          <li><strong>Tanker</strong> — oil, chemicals, LNG; larger tankers are more restrictive</li>
          <li><strong>Specialized</strong> — Reefers for refrigerated cargo, RoRo vessels</li>
          <li><strong>Express</strong> — fast couriers; small capacity but high-value container goods</li>
        </ul>
        <h4>Capacity &amp; Fuel</h4>
        <p>Ship capacity is measured in TEU. Larger ships carry more but cost more to operate. Every ship has a fuel range — refuel at any port's <strong>Services</strong> tab before you run out.</p>
        <div class="help-callout">If your ship runs out of fuel at sea it becomes stranded. Always check your range before setting sail!</div>
        <h4>Condition &amp; Repair</h4>
        <p>Ship condition degrades as you travel. Low condition reduces performance. Repair your ships at port via the <strong>Services</strong> tab. Repairs take time, running alongside loading and refuelling — and bigger jobs take disproportionately longer, so topping up a little at each stop beats one long overhaul. Engineers reduce day-to-day wear and speed up repairs.</p>
        <h4>Surveys, Inspections &amp; Dry Dock</h4>
        <ul>
          <li><strong>Annual survey</strong> — done automatically at the first repair port once due. Below 50% condition, the ship is held until repaired.</li>
          <li><strong>Port inspections</strong> — random checks at repair ports, more likely for run-down ships. Below 40% condition: a fine, and the ship is detained until repaired to 50%.</li>
          <li><strong>Dry dock</strong> — mandatory every 2–5 years depending on ship size, at shipyard ports. Skipping it means daily fines and being held at the next shipyard.</li>
        </ul>
        <div class="help-callout">Senior and expert engineers handle detentions on their own when you can afford it. Refuelling takes time too, but runs alongside loading.</div>
        <h4>Port Size Limits</h4>
        <p>Larger ships cannot dock at smaller ports. Tiny ports only accept ships up to 100 TEU, small up to 500, medium up to 2,000, and large up to 8,000. Major ports accept any size.</p>
        <h4>Ship Broker</h4>
        <p>Buy and sell ships at the Ship Broker, available at ports with a shipyard. Research unlocks access to larger and more specialized vessels.</p>`;

      case 'crew': return `
        <h4>Crew System</h4>
        <p>Hire crew members at ports to boost your ships' performance. Visit the <strong>Services</strong> tab at any port to access the Crew Hiring Office. Crew availability varies by port size.</p>
        <h4>Shared Crew Pools</h4>
        <p>Crew candidates are shared across all players. If another player hires the expert engineer at a port before you, they're gone — check back after a day for new candidates to arrive.</p>
        <h4>Crew Roles</h4>
        <ul>
          <li><strong>Navigator</strong> — increases ship speed and reduces fuel consumption</li>
          <li><strong>Engineer</strong> — reduces hull wear; senior auto-refuel/repair; expert finds best prices</li>
          <li><strong>Market Analyst</strong> — fleet-wide trade intelligence and profit tracking</li>
        </ul>
        <h4>Hiring &amp; Contracts</h4>
        <ul>
          <li>Each crew member is hired on a <strong>1-year contract</strong></li>
          <li>Salary is paid daily; terminating early incurs a penalty</li>
          <li>Larger ports have bigger crew pools and higher-tier candidates</li>
        </ul>
        <div class="help-callout">Each crew role requires a Tier 2 research unlock. Market Analysts are fleet-wide!</div>`;

      case 'contracts': return `
        <h4>Contracts</h4>
        <p>Contracts pay you to pick up cargo at an origin port and deliver it to a destination within a deadline.</p>
        <h4>Shared Contract Pool</h4>
        <p>Available contracts are shared across all players. Once you accept a contract it's yours — but so can another player accept it first. Fast decisions matter!</p>
        <ul>
          <li>Check the <strong>Contracts</strong> tab for available offers</li>
          <li>Accept a contract, load the cargo, then deliver before the deadline</li>
          <li>Plan your route and fuel carefully to avoid delays</li>
        </ul>
        <h4>Research</h4>
        <p>The <strong>Research</strong> tab lets you invest in new technologies that improve your fleet:</p>
        <ul>
          <li><strong>Fuel efficiency</strong> — reduce fuel consumption per voyage</li>
          <li><strong>Logistics</strong> — faster and cheaper loading and unloading</li>
          <li><strong>Navigation</strong> — improved route planning and speed</li>
          <li><strong>Permits</strong> — unlock access to larger and specialized ship types</li>
        </ul>
        <div class="help-callout">Some ships require specific research permits before you can purchase them.</div>`;

      case 'controls': return `
        <h4>Map Controls</h4>
        <ul>
          <li><strong>Click a port marker</strong> — select port and open port details</li>
          <li><strong>Click a ship marker</strong> — select that ship in the fleet panel</li>
          <li>Scroll to zoom, drag to pan the map</li>
        </ul>
        <h4>The Live World</h4>
        <p>SeaTrader runs on a <strong>persistent live server</strong>. There is no pause — the world advances 24/7 for all players. Closing your browser tab doesn't pause your ships; they keep sailing and your routes keep running.</p>
        <p>The <strong>live indicator</strong> in the top-left shows your connection status:</p>
        <ul>
          <li>Green dot — connected and receiving live updates</li>
          <li>Yellow dot — reconnecting (connection was lost, retrying automatically)</li>
          <li>Red dot — offline (server unreachable)</li>
        </ul>
        <h4>Keyboard</h4>
        <ul>
          <li><kbd>Esc</kbd> — Close modal or cancel action</li>
        </ul>`;

      default: return '';
    }
  }
}
